import {
  ElementTracker,
  ElementTrackerType,
  InputFieldsType,
  isZipCodeValid,
  SECRET_INPUT,
} from '@enaratech/funnel-helper';
import { Grid, TextField } from '@mui/material';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Dispatch, FC, ReactElement, SetStateAction, useEffect, useState } from 'react';
import { StripeTextField } from 'src/components/Common/Inputs/StripeInput/StripeTextField';
import { useRoutePath } from 'src/hooks/useRoutePath';
import { PaymentErrors, PaymentForm, PaymentMethodResult } from './Payment.types';

// Must match the `field` value of the zip code entry in `inputFields.ts`.
const ZIP_CODE_FIELD = 'zipCode';

type Props = {
  patient: string;
  inputs: InputFieldsType[];
  setCanContinue: (value: boolean) => void;
  form: PaymentForm;
  setForm: Dispatch<SetStateAction<PaymentForm>>;
  getPaymentMethod: (paymentMethod: PaymentMethodResult) => void;
};

const PaymentInputs: FC<Props> = ({
  patient,
  inputs,
  setCanContinue,
  form,
  setForm,
  getPaymentMethod,
}): ReactElement => {
  const [errors, setErrors] = useState<PaymentErrors | {}>({});

  const stripe = useStripe();
  const elements = useElements();

  const routePath = useRoutePath();

  const canContinue = (): void => {
    if (
      Object.values(errors).every((i) => i.length === 0) &&
      Object.values(form).every((i) => i.length > 0)
    ) {
      setCanContinue(true);
      createPaymentMethod();
    } else {
      setCanContinue(false);
    }
  };

  const createPaymentMethod = async () => {
    var cardElement = elements?.getElement(CardNumberElement);
    const paymentMethodResult = await stripe!.createPaymentMethod({
      billing_details: {
        name: patient,
        address: {
          line1: form.street,
          city: form.city,
          state: form.state,
          postal_code: form.zipCode,
        },
      },
      type: 'card',
      card: cardElement!,
    });

    getPaymentMethod(
      paymentMethodResult?.error
        ? { error: paymentMethodResult?.error }
        : { stripePaymentMethod: { id: `${paymentMethodResult?.paymentMethod.id}` } }
    );
  };

  const getFieldError = (field: string, value: string): string => {
    if (!value) {
      return `The ${field.toLowerCase()} is required`;
    }

    if (field === ZIP_CODE_FIELD && !isZipCodeValid(value)) {
      return 'Please enter a valid zip code';
    }

    return '';
  };

  // Stripe elements report completeness rather than a readable value, so this keeps
  // the (field, stripe, value) shape StripeTextField's `onFieldEmpty` expects.
  const validateStripeField = (field: string, _stripe: boolean, isComplete: boolean) => {
    setErrors((previous) => ({
      ...previous,
      [field]: isComplete ? '' : `The ${field.toLowerCase()} is required`,
    }));
  };

  // The value comes from the event, not from `form`: the paired `setForm` has not been
  // applied yet when this runs, so reading `form[field]` validates the previous keystroke.
  const validateTextField = (field: string, value: string) => {
    setErrors((previous) => ({
      ...previous,
      [field]: getFieldError(field, value),
    }));
  };

  // Prefilled values are never typed, so change/blur validation alone would let them
  // through: the zip code arrives from the member profile and the partner-url and
  // referral flows populate it without ever running it past `isZipCodeValid`. Seed
  // errors from the initial values, skipping empty fields so an untouched form does
  // not open covered in "is required" — `canContinue` already gates on presence.
  useEffect(() => {
    setErrors((previous) => ({
      ...previous,
      ...Object.fromEntries(
        Object.entries(form)
          .filter(([, value]) => !!value)
          .map(([field, value]) => [field, getFieldError(field, value)])
      ),
    }));
    // Intentionally mount-only: this validates the initial values, and every later
    // change is already validated by validateTextField.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `form` has to be a dependency too — `canContinue` reads it, and validation now
  // runs off the event value, so the state it checks must be the post-update one.
  useEffect(() => {
    canContinue();
  }, [errors, form]);

  return (
    <Grid container justifyContent={'space-between'} spacing={3} className='inputs-payment'>
      {inputs.map(({ label, field, size, inputType, name, track, component }) => (
        <Grid item xs={12} sm={size} key={`${field}-payment`}>
          {inputType === 'stripe' ? (
            <ElementTracker
              routePath={routePath}
              name={name}
              type={ElementTrackerType.Blurrable}
              value={!track ? SECRET_INPUT : undefined}>
              <StripeTextField
                id={`${field}-payment`}
                field={field}
                label={label}
                stripeElement={component}
                onFieldEmpty={validateStripeField}
              />
            </ElementTracker>
          ) : (
            <ElementTracker
              routePath={routePath}
              name={name}
              type={ElementTrackerType.Blurrable}
              value={!track ? SECRET_INPUT : undefined}>
              <TextField
                id={`${field}-payment`}
                label={label}
                value={form![field as keyof typeof form]}
                variant='filled'
                // `type='number'` used to reject the dash, making ZIP+4 impossible to
                // type even though `isZipCodeValid` accepts it.
                type='text'
                inputProps={
                  field === ZIP_CODE_FIELD ? { inputMode: 'numeric', maxLength: 10 } : undefined
                }
                autoComplete='invalidText' // To avoid autoComplete
                onChange={(e) => {
                  setForm({
                    ...form,
                    [field]: e.target.value,
                  });
                  validateTextField(field, e.target.value);
                }}
                error={!!errors[field as keyof typeof errors]}
                onBlur={(e) => {
                  setForm({
                    ...form,
                    [field]: e.target.value,
                  });
                  validateTextField(field, e.target.value);
                }}
                helperText={
                  !!errors[field as keyof typeof errors] ? errors[field as keyof typeof errors] : ''
                }
              />
            </ElementTracker>
          )}
        </Grid>
      ))}
    </Grid>
  );
};

export default PaymentInputs;
